import { useEffect, useRef, useState } from "react";

/**
 * Hook to monitor audio level from a MediaStreamTrack
 * Works with both React and Preact (via preact/compat)
 */
export function useAudioLevel(track: MediaStreamTrack | null) {
  const levelRef = useRef(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [hasTrack, setHasTrack] = useState(false);
  const animationFrameIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!track) {
      levelRef.current = 0;
      setAudioLevel(0);
      setHasTrack(false);
      return;
    }

    setHasTrack(true);

    let audioContext: AudioContext | null = null;

    try {
      audioContext = new AudioContext();

      if (audioContext.state === "suspended") {
        audioContext.resume().catch((err) => {
          console.error("Failed to resume AudioContext:", err);
        });
      }

      const source = audioContext.createMediaStreamSource(
        new MediaStream([track]),
      );
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);

      const frequencyData = new Uint8Array(analyser.frequencyBinCount);

      const analyzeAudio = () => {
        analyser.getByteFrequencyData(frequencyData);

        const sum = frequencyData.reduce((acc, val) => acc + val, 0);
        const averageVolume = sum / frequencyData.length;
        const normalizedLevel = Math.min(averageVolume / 255, 1);

        levelRef.current = normalizedLevel;
        setAudioLevel(normalizedLevel);

        animationFrameIdRef.current = requestAnimationFrame(analyzeAudio);
      };

      analyzeAudio();
    } catch (e) {
      console.error("Audio analysis error:", e);
    }

    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
      if (audioContext) {
        audioContext.close();
      }
    };
  }, [track]);

  return { levelRef, audioLevel, hasTrack };
}
